import { ControllerInterface } from "csi/action/Controller";
import JsonResponse from "csi/comm/JsonResponse";
import { CheckboxTreeNode } from "designSystem/components/checkbox/CheckboxTree";
import { ILocaleProvider } from "designSystem/locale";
import { RadioOption } from "designSystem/components/radio/RadioGroup";
import config from "csi/config";
import def from "csi/def";
import { View } from "csi/def/View";
import { ControllerSaveFunction } from "csi/middleware/common";
import reflection from "csi/reflection";
import { MassUpdateWizardStore } from "csi/store/MassUpdateWizardStore";
import { ICache, cnl } from "csi/typings";
import { createContext, useContext } from "react";
import { massUpdateStyleSizeChart } from "./MassUpdateStyleSizeChartService";
import FormData from "csi/action/FormData";

export class MassUpdateStyleSizeChartStore extends MassUpdateWizardStore {
	constructor(
		cache: ICache,
		formData: FormData,
		onClose: () => void,
		onSave: ControllerSaveFunction,
		locale: ILocaleProvider,
		revisionURL: cnl,
		itemsMap: Map<string, CheckboxTreeNode>,
	) {
		super(onClose, onSave, cache, formData, locale, "MassUpdateStylesSizeChart", revisionURL, itemsMap);
		this.actionMessageRes = "MassUpdateStylesSizeChart_Dimensions_Selected";
		this.validationMsgRes = "MassUpdateStylesSizeChart_SelectionEmptyError";
	}

	/** get Sizechart.subtype Lock Dimension List value */
	get isLockShapeDimensionList(): boolean {
		const sizeChart = this.cache.getPathNode(this.revisionURL, "Child:__Parent__");
		const lockShapeDimensionList = config.getSubtypeSetting(sizeChart?.Subtype, "LockShapeDimensionList");
		return lockShapeDimensionList as boolean;
	}

	/** 
	 * @override
	 *  Return table view definition to show on 2nd page of wizard
	 * */
	_defLinkedBOMsView = (): View => {
		const rc = reflection.getClass("SizeChart");
		const defView = def.getClassViewDef(rc, "StyleSizeChartsGroupByParent") as View;

		defView.setFormData(this.formData);
		return defView;
	};

	/** 
	 * @override 
	 * extension point to get Radio option array. 
	 * Include only PushAll option if LockShapeDimensionList == true 
	 * */
	_massUpdateOptions = (): RadioOption<boolean>[] => {
		const options =  [
			{ 
				name: this.locale.resource("MassUpdateStylesSizeChart_PushAllDimensions"),
				helperTextRes: "MassUpdateStylesSizeChart_PushAllDimensions_HelperText",
				value: true,
			},
		];
		
		if (!this.isLockShapeDimensionList) {
			options.push({ 
				name: this.locale.resource("MassUpdateStylesSizeChart_UpdateDimensionsOnly"),
				helperTextRes: "MassUpdateStylesSizeChart_UpdateDimensionsOnly_HelperText",
				value: false, 
			});
		}
		return options;
	};

	/** 
	 * @override
	 *  extension point to call the Mass Update operation.
	 * */
	_massUpdateOperation = async (createRevision: boolean, urlRevision: cnl, pushAllPlacements: boolean, itemsSelected: cnl[], targetsSelected: cnl[]): Promise<JsonResponse> => {
		return massUpdateStyleSizeChart(createRevision, urlRevision, pushAllPlacements, itemsSelected, targetsSelected);
	};
}

export const MassUpdateStyleSizeChartStoreContext = createContext<MassUpdateStyleSizeChartStore>(undefined);
export const useMassUpdateStyleSizeChartStore = (): MassUpdateStyleSizeChartStore => useContext(MassUpdateStyleSizeChartStoreContext);
